import React, { type JSX } from 'react';
import { useController } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { DEFAULT_LOCALE } from '@rover/constants/src/i18n/locale';
import { Box, Link, Paragraph } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import SelectField from '@rover/kibble/forms/fields/SelectField';

import { useCountryName } from './hooks/useCountryName';
import CountryChangeAlert from './CountryChangeAlert';

export interface CountrySectionProps<Z extends ZodSchemaOrEffects> {
  countryField: ConfigureFieldInterface<Z>;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
  locale?: Locale;
  disableCountry?: boolean;
  getValues?: (name: string) => string;
  suppressInlineAlerts?: boolean;
}

const CountrySection = <Z extends ZodSchemaOrEffects>({
  countryField,
  countryCheckboxesField,
  locale = DEFAULT_LOCALE,
  disableCountry = false,
  suppressInlineAlerts = false,
}: CountrySectionProps<Z>): JSX.Element => {
  const { t } = useLingui();
  const { field: countryFieldValue, fieldState: countryFieldState } = useController({
    control: countryField.control,
    name: countryField.name,
  });

  const { countryOptions, countryName } = useCountryName(locale, countryFieldValue.value);

  return (
    <>
      <Box position="relative">
        <SelectField
          {...countryField}
          primaryLabel={t`Country`}
          options={countryOptions}
          placeholder={t`Select country`}
          disabled={disableCountry}
          onChange={(selectedValue) => {
            countryFieldValue.onChange(selectedValue);
          }}
        />
        {disableCountry && (
          <Paragraph size="100" textColor="tertiary" mt="1x">
            <Trans>
              Please contact{' '}
              <Link
                size="50"
                fontWeight="regular"
                asInlineLink
                href="https://support.rover.com/hc/en-us/requests/new"
              >
                Rover Support
              </Link>{' '}
              to change your home country.
            </Trans>
          </Paragraph>
        )}
      </Box>
      {!suppressInlineAlerts && countryFieldState.isDirty && (
        <CountryChangeAlert
          countryName={countryName}
          countryCheckboxesField={countryCheckboxesField}
        />
      )}
    </>
  );
};

export default CountrySection;
